import { useState, useEffect, cloneElement } from "react";
import TruckCustomizer from "@/components/TruckCustomizer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Star, Check } from "lucide-react";
import { NavigationMenuItem } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

// Define section IDs
const SECTION_IDS = {
  VISUALIZER: "visualizer",
};

const Splash = () => {
  const [activeSection, setActiveSection] = useState<string>(SECTION_IDS.VISUALIZER);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setActiveSection(sectionId);
    // setMobileMenuOpen(false);
  };

  useEffect(() => {
    document.body.style.backgroundColor = "#000";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-black p-4">
      <div className="w-full max-w-6xl flex flex-col items-center space-y-16">
        {/* Logo */}
        <div className="w-full max-w-[320px] mb-2">
          <img src="/logo.png" alt="MEDIA TRUCK Logo" className="w-full" />
        </div>

        {/* Logo Visualizer Section - Main Focus */}
        <section id="visualizer" className="py-20 px-4">
          <div className="container space-y-12">
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <span className="inline-block bg-primary/20 text-primary px-4 py-1.5 rounded-full text-sm font-medium">
                Interactive Designer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gradient">Logo Visualizer</h2>
              <p className="text-lg text-muted-foreground">
                Upload your logos and see them come to life on our premium trucks.
                Adjust size, position and get a realistic preview before making your decision.
              </p>
            </div>

            <TruckCustomizer />

            <div className="grid gap-8 mt-16 md:grid-cols-3">
              {[
                {
                  title: "Precision Preview",
                  desc: "High-fidelity visualizations that accurately represent how your logo will appear on our trucks.",
                  icon: <Star className="h-6 w-6" />
                },
                {
                  title: "Instant Feedback",
                  desc: "See changes in real time as you adjust the size and position of your brand logos.",
                  icon: <Check className="h-6 w-6" />
                },
                {
                  title: "Expert Advice",
                  desc: "Get recommendations from our design specialists on the best placement for maximum brand visibility.",
                  icon: <Users className="h-6 w-6" />
                }
              ].map((feature, i) => (
                <Card key={i} className="glass border-primary/20 overflow-hidden hover-scale">
                  <CardContent className="p-6 space-y-4">
                    <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                      {cloneElement(feature.icon, { className: "h-6 w-6 text-primary" })}
                    </div>
                    <h3 className="text-xl font-bold">{feature.title}</h3>
                    <p className="text-muted-foreground">
                      {feature.desc}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <NavigationMenuItem>
          <Button
            variant="ghost"
            className={cn(
              "relative nav-link",
              activeSection === "visualizer" && "text-primary font-medium"
            )}
            onClick={() => scrollToSection("visualizer")}
          >
            Truck Customizer
            {activeSection === "visualizer" && (
              <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
            )}
          </Button>
        </NavigationMenuItem>

        <Button
          variant="ghost"
          size="lg"
          className={cn("w-full justify-start text-lg font-medium",
            activeSection === "visualizer" && "bg-primary/10 text-primary"
          )}
          onClick={() => scrollToSection("visualizer")}
        >
          Truck Customizer
        </Button>

        <p className="text-xs text-muted-foreground mt-1">
          Designed & Developed by <a href="https://github.com/burgil/" target="_blank" rel="nofollow noopener noreferrer" className="text-primary font-medium hover:underline">Burgil</a>
        </p>
      </div>
    </div>
  );
};

export default Splash;
